import moment from "moment";
import { Observable, of, from, forkJoin } from "rxjs";
import { mergeMap, flatMap, map, bufferCount, tap } from 'rxjs/operators';
import { maxBy } from 'lodash-es';
import { Question, QuestionWithDropdownAndAnswers, TaxReturn, TaxReturnService } from "../../../swaggerServices";
import { ITaxReturn } from "./tax-return/model/i-tax-return";
import { TaxReturnStatus } from "./tax-return/model/status";
import { makeName } from "../../shared/util/makeName";
import { ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { IFormField } from "../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { parseMessageForDocumentsAndUrls, Parts } from "./tax-return/urls";
import { IComment } from "./tax-return/subcategory/comments/comments.component";
import { getResolvedDocument, inDocumentCache, updateDocumentCache } from "./tax-return/document-cache";
import { GlobalConfig } from "../config/config-states";

const dateFormat = "DD/MM/YYYY h:mm:ssa";

// Get a full tax return, and apply additional pre-processing over the raw swagger service.
export const getFullTaxReturn = (taxReturnService: TaxReturnService, taxReturnId: number, subcategoryId: number | null = null, snapshotId: number | null = null, requestFullData: boolean = false, topLevel: boolean = false): Observable<ITaxReturn> => {
  if(!topLevel && !subcategoryId){
    return taxReturnService.getTaxReturnById(taxReturnId, "true", "true").pipe(
      map((taxReturn: ITaxReturn) => {
        initializeTaxReturn(taxReturn, snapshotId, requestFullData);
        return taxReturn;
      }));
  } else if (topLevel) {
    return taxReturnService.getTaxReturnTopLevelById(taxReturnId).pipe(
      map((taxReturn: ITaxReturn) => {
        initializeTaxReturn(taxReturn, snapshotId, requestFullData);
        return taxReturn;
      }));
  } else {
    return taxReturnService.getTaxReturnByIdAndSubcategoryId(taxReturnId, subcategoryId).pipe(
      map((taxReturn: ITaxReturn) => {
        initializeTaxReturn(taxReturn, snapshotId, requestFullData);
        return taxReturn;
      }));
  }
}

export const setQuestionAndAnswerLookups = (taxReturn: ITaxReturn) => {
  taxReturn.questionsById = {};
  taxReturn.answersById = {};
  if (taxReturn.questions) {
    taxReturn.questions.forEach(q => {
      taxReturn.questionsById[q.id] = q;
      q.answers.forEach(a => taxReturn.answersById[a.id] = a);
    });
  }
};

const isGroupDeleted = (taxReturn, g) => {
  if (g.id === 0) {
    return false;
  }
  const parentAnswerId = g.id;
  if (taxReturn.answersById[parentAnswerId] && taxReturn.answersById[parentAnswerId].answer) {
    return false;
  }
  return g.subgroups.every(sg => sg.questions.length === 0 || sg.questions.every(q => {
    if (q.isRowId) {
      return true;
    }
    const answers = q.answers.map(a => taxReturn.answersById[a.id]);
    return answers.length === 0 || answers.every(a => a.answer === '' || a.answer == null);
  }));
};

const isGhost = (taxReturn, g) => g.subgroups.every(sg => sg.questions.length === 0 || sg.questions.every(q => {
  const answers = q.answers.map(a => taxReturn.answersById[a.id]);
  return answers.length === 0 || answers.every(a => isGhostAnswer(a));
}));

export const uniqueDocumentsForAnswers = as => {
  const documentIds = {};
  as.forEach(a => {
    if (a.comments) {
      a.comments.forEach(c => {
        if (c.documents) {
          c.documents.forEach(d => {
            documentIds[d.id] = true;
          });
        }
      })
    }
  }

  );
  return documentIds;
};

export const uniqueCommentsForAnswers = as => {
  const commentIds = {};
  as.forEach(a => {
    if (a.comments) {
      a.comments.forEach(c => {
        if (!c.answerFlagId && !c.answerWarningId) {
          commentIds[c.id] = true;
        }
    })
  }});
  return commentIds;
};

export const uniqueOpenWarningsForAnswers = as => {
  const awIds = {};
  as.forEach(a => {
    if (a.answerWarnings) {
      a.answerWarnings.forEach(aw => {
        if (aw.status === 0) {
          awIds[aw.id] = true;
        }
      });
    }
  }
  );
  return awIds;
};

export const uniqueClosedWarningsForAnswers = as => {
  const awIds = {};
  as.forEach(a => {
    if (a.answerWarnings) {
      a.answerWarnings.forEach(aw => {
        if (aw.status > 0) {
          awIds[aw.id] = true;
        }
      });
    }
  }
  );
  return awIds;
};

export const uniqueOpenFlagsForAnswers = as => {
  const afIds = {};
  as.forEach(a => {
    if (a.answerFlags) {
      a.answerFlags.forEach(af => {
        if (af.status === 0) {
          afIds[af.id] = true;
        }
      })
    }
  });
  return afIds;
};

export const uniqueClosedFlagsForAnswers = as => {
  const afIds = {};
  as.forEach(a => {
    if (a.answerFlags) {
      a.answerFlags.forEach(af => {
        if (af.status > 0) {
          afIds[af.id] = true;
        }
      })
    }
  });
  return afIds;
};

export const mergeUniqueIds = arr => {
  const uniqueIds = {};
  arr.forEach(idsObj => {
    Object.keys(idsObj).forEach(id => {
      uniqueIds[id] = true;
    });
  });
  return uniqueIds;
};

const initCommentUrlsAndDocuments = (taxReturn: ITaxReturn) => {
  const commentDataById = {}; // cache because comments can be repeated
  const allDocuments = {};
  Object.values(taxReturn.answersById).forEach((answer: any) => {
    answer.comments.forEach(comment => {
      let data = commentDataById[comment.id];
      if (data == null) {
        data = parseMessageForDocumentsAndUrls(comment.message, comment.attachments);
        commentDataById[comment.id] = data;
        data.documents.forEach(d => allDocuments[d.id] = d);
      }
      comment.parts = data.parts;
      comment.documents = data.documents;
      comment.attachments = data.attachments;
    });
  });
  taxReturn.documents = Object.values(allDocuments);
};

const initQuestions = (taxReturn: ITaxReturn) => {
  taxReturn.categories = taxReturn.categories.filter(c => c.order != null)
    .sort((a, b) => a.order - b.order)
    .concat(taxReturn.categories.filter(c => c.order == null));

  taxReturn.categories.forEach(c => {
    c.subcategories = c.subcategories.filter(sc => sc.order != null)
      .sort((a, b) => a.order - b.order)
      .concat(c.subcategories.filter(sc => sc.order == null));

    c.subcategories.forEach(sc => {
      const qs = sc.questions
        .map(q => {
          const question = {
            ...q,
            ...taxReturn.questionsById[q.id],
          };
          const answersById = {};
          if (!question.answers) question.answers = [];
          question.answers.forEach(a => answersById[a.id] = a);
          const answers = q.answers.map(a => answersById[a.id]).filter(x => x);
          return {
            ...question,
            answers,
          };
        });

      sc.questions = qs.filter(q => q.subCategoryOrder.order != null)
        .sort((a, b) => a.subCategoryOrder.order - b.subCategoryOrder.order)
        .concat(qs.filter(q => q.subCategoryOrder.order == null));

      if(!taxReturn.categoryCounts){
        sc.groups.forEach(group => {
          group.subgroups = group.subgroups.filter(g => (g.subgroupSubcategoryOrder && g.subgroupSubcategoryOrder.order != null))
            .sort((a, b) => a.subgroupSubcategoryOrder.order - b.subgroupSubcategoryOrder.order)
            .concat(group.subgroups.filter(q => (q.subgroupSubcategoryOrder && q.subgroupSubcategoryOrder.order == null)));

          // subgroup questions
          group.subgroups.forEach(subgroup => {
            const qs = subgroup.questions
              .map(q => {
                const question = {
                  ...q,
                  ...taxReturn.questionsById[q.id],
                };
                const answersById = {};
                question.answers.forEach(a => answersById[a.id] = a);
                const answers = q.answers.map(a => answersById[a.id]).filter(x => x);
                return {
                  ...question,
                  answers,
                };
              });
            subgroup.questions = qs
              .filter(q => q.order != null)
              .sort((a, b) => a.order - b.order)
              .concat(qs.filter(q => q.order == null));
          });
        });
      }
    });
  });
};
// appends full questions and answers
// sort by order and moves null order to last
const initQuestionsAndCounts = (taxReturn: ITaxReturn) => {
  taxReturn.categories.forEach(c => {
    const catDocumentsArr = [];
    const catCommentsArr = [];
    const catFlagsArr = [];
    const catWarningsArr = [];
    const catClosedFlagsArr = [];
    const catClosedWarningsArr = [];

    c.subcategories.forEach(sc => {
      const scDocumentsArr = [];
      const scCommentsArr = [];
      const scFlagsArr = [];
      const scWarningsArr = [];
      const scClosedFlagsArr = [];
      const scClosedWarningsArr = [];
      const scNotesArr = taxReturn.notes.filter(n => n.status === 0 && n.subcategories.some(_sc => _sc.id === sc.id));
      if(!taxReturn.categoryCounts){
        sc.groups.forEach(group => {
          const tabAnswerAsArray = group.id ? [taxReturn.answersById[group.id]] : [];
          const groupDocumentsArr = [uniqueDocumentsForAnswers(tabAnswerAsArray)];
          const groupCommentsArr = [uniqueCommentsForAnswers(tabAnswerAsArray)];
          const groupFlagsArr = [uniqueOpenFlagsForAnswers(tabAnswerAsArray)];
          const groupWarningsArr = [uniqueOpenWarningsForAnswers(tabAnswerAsArray)];
          const groupClosedFlagsArr = [uniqueClosedFlagsForAnswers(tabAnswerAsArray)];
          const groupClosedWarningsArr = [uniqueClosedWarningsForAnswers(tabAnswerAsArray)];
          // subgroup questions
          group.subgroups.forEach(subgroup => {
            const subgroupAnswers = [];
            const subgroupFileQuestion = {};
            subgroup.questions.forEach(q => {
              subgroupAnswers.push(...q.answers);
              //count file question documents
              if(q.displayFieldType === 'file' && q.answers) {
                q.answers.map(a => {
                  if(a.answer) subgroupFileQuestion[a.answer] = true
                })
              }
            });
            let subgroupDocuments = uniqueDocumentsForAnswers(subgroupAnswers);
            subgroupDocuments = {...subgroupDocuments, ...subgroupFileQuestion};
            const subgroupComments = uniqueCommentsForAnswers(subgroupAnswers);
            const subgroupFlags = uniqueOpenFlagsForAnswers(subgroupAnswers);
            const subgroupWarnings = uniqueOpenWarningsForAnswers(subgroupAnswers);
            subgroup.documentCount = Object.keys(subgroupDocuments).length;
            subgroup.commentCount = Object.keys(subgroupComments).length;
            subgroup.flagCount = Object.keys(subgroupFlags).length;
            subgroup.warningCount = Object.keys(subgroupWarnings).length;

            const subgroupClosedFlags = uniqueClosedFlagsForAnswers(subgroupAnswers);
            const subgroupClosedWarnings = uniqueClosedWarningsForAnswers(subgroupAnswers);
            subgroup.closedFlagCount = Object.keys(subgroupClosedFlags).length;
            subgroup.closedWarningCount = Object.keys(subgroupClosedWarnings).length;

            groupDocumentsArr.push(subgroupDocuments);
            groupCommentsArr.push(subgroupComments);
            groupFlagsArr.push(subgroupFlags);
            groupWarningsArr.push(subgroupWarnings);
            groupClosedFlagsArr.push(subgroupClosedFlags);
            groupClosedWarningsArr.push(subgroupClosedWarnings);
          });

          const groupDocuments = mergeUniqueIds(groupDocumentsArr);
          const groupComments = mergeUniqueIds(groupCommentsArr);
          const groupFlags = mergeUniqueIds(groupFlagsArr);
          const groupWarnings = mergeUniqueIds(groupWarningsArr);
          const groupClosedFlags = mergeUniqueIds(groupClosedFlagsArr);
          const groupClosedWarnings = mergeUniqueIds(groupClosedWarningsArr);
          group.documentCount = Object.keys(groupDocuments).length;
          group.commentCount = Object.keys(groupComments).length;
          group.flagCount = Object.keys(groupFlags).length;
          group.warningCount = Object.keys(groupWarnings).length;
          group.closedFlagCount = Object.keys(groupClosedFlags).length;
          group.closedWarningCount = Object.keys(groupClosedWarnings).length;
          scDocumentsArr.push(groupDocuments);
          scCommentsArr.push(groupComments);
          scFlagsArr.push(groupFlags);
          scWarningsArr.push(groupWarnings);
          scClosedFlagsArr.push(groupClosedFlags);
          scClosedWarningsArr.push(groupClosedWarnings);
        });
      }
      const scDocuments = mergeUniqueIds(scDocumentsArr);
      const scComments = mergeUniqueIds(scCommentsArr);
      const scFlags = mergeUniqueIds(scFlagsArr);
      const scWarnings = mergeUniqueIds(scWarningsArr);
      const scClosedFlags = mergeUniqueIds(scClosedFlagsArr);
      const scClosedWarnings = mergeUniqueIds(scClosedWarningsArr);
      if(taxReturn.categoryCounts){
        sc.documentCount = taxReturn.categoryCounts.find(c => c.subcategoryId === sc.id)?.documentCount;
        sc.commentCount = taxReturn.categoryCounts.find(c => c.subcategoryId === sc.id)?.commentCount;
        sc.flagCount = taxReturn.categoryCounts.find(c => c.subcategoryId === sc.id)?.flagCount;
        sc.warningCount = taxReturn.categoryCounts.find(c => c.subcategoryId === sc.id)?.warningCount;
      } else {
        sc.documentCount = Object.keys(scDocuments).length;
        sc.commentCount = Object.keys(scComments).length;
        sc.flagCount = Object.keys(scFlags).length;
        sc.warningCount = Object.keys(scWarnings).length;
        sc.closedFlagCount = Object.keys(scClosedFlags).length;
        sc.closedWarningCount = Object.keys(scClosedWarnings).length;
        catDocumentsArr.push(scDocuments);
        catCommentsArr.push(scComments);
        catFlagsArr.push(scFlags);
        catWarningsArr.push(scWarnings);
        catClosedFlagsArr.push(scClosedFlags);
        catClosedWarningsArr.push(scClosedWarnings);
      }
      sc.notesCount = scNotesArr.length;
    });
    if(taxReturn.categoryCounts){
      c.documentCount = c.subcategories.reduce((acc, sc) => acc+sc.documentCount)
      c.commentCount = c.subcategories.reduce((acc, sc) => acc+sc.commentCount)
      c.flagCount = c.subcategories.reduce((acc, sc) => acc+sc.flagCount)
      c.warningCount = c.subcategories.reduce((acc, sc) => acc+sc.warningCount)
    } else {
      const catDocuments = mergeUniqueIds(catDocumentsArr);
      const catComments = mergeUniqueIds(catCommentsArr);
      const catFlags = mergeUniqueIds(catFlagsArr);
      const catWarnings = mergeUniqueIds(catWarningsArr);
      const catClosedFlags = mergeUniqueIds(catClosedFlagsArr);
      const catClosedWarnings = mergeUniqueIds(catClosedWarningsArr);
      c.documentCount = Object.keys(catDocuments).length;
      c.commentCount = Object.keys(catComments).length;
      c.flagCount = Object.keys(catFlags).length;
      c.warningCount = Object.keys(catWarnings).length;
      c.closedFlagCount = Object.keys(catClosedFlags).length;
      c.closedWarningCount = Object.keys(catClosedWarnings).length;
    }
  });
};

const isGhostAnswer = answer => {
  return answer.id == null ||
    (
      !answer.overrideStatus &&
      (answer.answer == null || answer.answer === "") &&
      // just flags and warnings, not comments
      // answer.comments.length === 0 &&
      answer.parentAnswerId !== null &&
      answer.answerFlags &&
      answer.answerFlags.length === 0 &&
      answer.answerWarnings &&
      answer.answerWarnings.length === 0
    );
};

const isGhostAnswerWarnings = answer => {
  return answer.id == null ||
    (
      !answer.overrideStatus &&
      (answer.answer == null || answer.answer === "") &&
      answer.parentAnswerId !== null
    );
};

// find questions in taxReturn and initialise table
export const initTableForSingleTaxReturn = (taxReturn: TaxReturn, parentQ: any, answersByQuestionIdParentAnswerIdAndTaxReturnId: any) => {
  const allTableQuestionIds = {};
  const tableSubGroup: {
    id?: number
    question?: QuestionWithDropdownAndAnswers
    name?: string
    isTable?: boolean
    questions?: QuestionWithDropdownAndAnswers[]
    rows?: Array<{ rowAnswer: any, cells: any }>
  } = {
    id: parentQ.id,
    name: parentQ.question,
    isTable: true,
    question: parentQ,
    questions: [],
  };
  allTableQuestionIds[parentQ.id] = true;
  // find the parent table question and its column questions
  taxReturn.questions.forEach(q => {
    if (q.parentId === parentQ.id) {
      if (q.displayFieldType === 'checkbox' && q.dataType === 'boolean') {
        tableSubGroup.questions.unshift(q);
      } else {
        tableSubGroup.questions.push(q);
      }
      allTableQuestionIds[q.id] = true;
    }
  });


  tableSubGroup.rows = parentQ.answers.map(rowAnswer => {
    const cellsByColQuestionId = {};
    tableSubGroup.questions.forEach(colQ => {
      const cellAnswer = answersByQuestionIdParentAnswerIdAndTaxReturnId[`${colQ.id}_${rowAnswer.id}_${taxReturn.id}`];
      cellsByColQuestionId[colQ.id] = (cellAnswer && cellAnswer.length) ? cellAnswer[0] : { answer: "" };
    });
    return { rowAnswer, cells: cellsByColQuestionId };
  })
    // NOTE (jramli)
    // RS-1199 there should not be any more ghost rows retrieved from the server
    // Logic left here in case we need to back out
    // (aaron) we're still getting ghost rows
    .filter(row => {
      // A ghost row is a row that is completely empty and was not manually added via override
      const isGhostRow = Object.values(row.cells).every(a => isGhostAnswerWarnings(a));
      return !isGhostRow;
    });
  return tableSubGroup;
};


// finds any table questions inside a subgroup and extracts it into its own subgroup above its original subgroup
export const initTables = (taxReturn: ITaxReturn) => {

  taxReturn.categories.forEach(c => {

    c.subcategories.forEach(sc => {

      sc.groups.forEach((g) => {
        const allTableQuestionIds = {};
        // loop backwards to prevent table group splicing breaking forward loop
        let i = (g.subgroups) ? g.subgroups.length : 0;
        while (i--) {
          const subgroup = g.subgroups[i];
          const tableQuestions = subgroup.questions.filter(q => q.dataType === 'table');
          const newSubgroups = tableQuestions.map(tq => {
            const tableSubGroup: {
              id?: number
              question?: IFormField
              name?: string
              hint?: string
              isTable?: boolean
              questions?: IFormField[]
              rows?: Array<{}>
            } = {
              id: tq.id,
              name: tq.question,
              hint: tq.hint,
              isTable: true,
              question: tq,
              questions: [],
            };
            allTableQuestionIds[tq.id] = true;

            // find the parent table question and its column questions
            subgroup.questions.forEach(q => {
              if (q.parentId === tq.id) {
                if (q.displayFieldType === 'checkbox' && q.dataType === 'boolean') {
                  tableSubGroup.questions.unshift(q);
                } else {
                  tableSubGroup.questions.push(q);
                }
                allTableQuestionIds[q.id] = true;
              }
            });

            const lookup = {};
            tableSubGroup.questions.forEach(colQ => {
              const answerLookup = {};
              colQ.answers.forEach(a => {
                answerLookup[a.parentAnswerId] = a;
              });
              lookup[colQ.id] = answerLookup;
            });
            tableSubGroup.rows = tq.answers.map(rowAnswer => {
              const cellsByColQuestionId = {};
              tableSubGroup.questions.forEach(colQ => {
                const cellAnswer = lookup[colQ.id][rowAnswer.id];
                cellsByColQuestionId[colQ.id] = cellAnswer || { answer: "" };
              });
              return { rowAnswer, cells: cellsByColQuestionId };
            })
              // NOTE (jramli)
              // RS-1199 there should not be any more ghost rows retrieved from the server
              // Logic left here in case we need to back out
              .filter(row => {
                // A ghost row is a row that is completely empty and was not manually added via override
                const isGhostRow = row.rowAnswer.overrideStatus === 0 &&
                  (row.cells.length === 0 || Object.values(row.cells).every(a => isGhostAnswer(a)));
                return !isGhostRow;
              });
            return tableSubGroup;
          });
          g.subgroups.splice(i, 0, ...newSubgroups);
        }

        // remove table questions in non-table subgroups
        g.subgroups.forEach(sg => {
          if (!sg.isTable) {
            sg.questions = sg.questions.filter(q => !allTableQuestionIds[q.id]);
          }
        });

        // remove subgroup if no remaining questions
        g.subgroups = g.subgroups.filter(sg => sg.questions.length > 0);
      });
    });
  });


};

// get count of active answer warnings plus field validation warnings for a subcategory or for all subcategories
export const getActiveWarnings = (taxReturn: ITaxReturn, subCategory?): number => {
  let count = 0;
  if (taxReturn.categoryCounts && subCategory) {
    const foundCategory = taxReturn.categoryCounts.find(sc => sc.subcategoryId === subCategory.id);
    return foundCategory ? foundCategory.warningCount : 0;
  }
  taxReturn && taxReturn.categories && taxReturn.categories.forEach(c => {
    (subCategory ? c.subcategories.filter(sc => sc.id === subCategory.id) : c.subcategories)
      .forEach(sc => {
        if (sc.warningCount) {
          count += sc.warningCount
          sc.warningscount = sc.warningCount
        } else {
          sc.warningsCount = 0; // validation warnings plus active answer warnings
          sc.groups.filter(g => g.id !== 0 && !g.deleted).forEach(g =>
            g.subgroups && g.subgroups.forEach(sg =>
              sg.questions && sg.questions.forEach(q =>
                q.answers && q.answers.forEach(a =>
                  a.answerWarnings && a.answerWarnings.forEach(aw => {
                    if (aw.status === 0) {
                      count++;
                      sc.warningsCount++;
                    }
                  })))));
        }
        if (sc.validationWarnings) {
          count += sc.validationWarnings.length;
          sc.warningsCount += sc.validationWarnings.length;
        }

      });
  });
  return count;
};

// get count of closed answer warnings plus field validation warnings for a subcategory or for all subcategories
export const getClosedWarnings = (taxReturn: ITaxReturn, subCategory?): number => {
  let count = 0;
  if (taxReturn.categoryCounts && subCategory) {
    const foundCategory = taxReturn.categoryCounts.find(sc => sc.subcategoryId === subCategory.id);
    return foundCategory ? foundCategory.warningCount : 0;
  }
  taxReturn && taxReturn.categories && taxReturn.categories.forEach(c => {
    (subCategory ? c.subcategories.filter(sc => sc.id === subCategory.id) : c.subcategories)
      .forEach(sc => {
        if (sc.warningCount && sc.status !== 0) {
          count += sc.warningCount
          sc.warningscount = sc.warningCount
        } else {
          sc.warningsCount = 0; // validation warnings plus active answer warnings
          sc.groups.filter(g => g.id !== 0 && !g.deleted).forEach(g =>
            g.subgroups && g.subgroups.forEach(sg =>
              sg.questions && sg.questions.forEach(q =>
                q.answers && q.answers.forEach(a =>
                  a.answerWarnings && a.answerWarnings.forEach(aw => {
                    if (aw.status !== 0) { // not active status
                      count++;
                      sc.warningsCount++;
                    }
                  })))));
        }
      });
  });
  return count;
};

// get count of all mandatory fields which have not been answered
const getValidationWarnings = (taxReturn: ITaxReturn) => {
  if (!taxReturn.questions) {
    return;
  }
  const dateStr = moment().format("MMMM Do YYYY, h:mm:ss a");
  const questionsById = taxReturn.questionsById;
  const answersById = taxReturn.answersById;

  const validatorsForQuestion = q => {
    const validators = [];
    if (q.mandatory) validators.push(Validators.required);
    if (q.min) validators.push(Validators.min(q.min));
    if (q.max) validators.push(Validators.max(q.max));
    if (q.minLength) validators.push(Validators.minLength(q.minLength));
    if (q.maxLength) validators.push(Validators.maxLength(q.maxLength));
    return validators;
  };

  const getValidationMessage = (e: ValidationErrors) => {
    let message;
    if (e.required) message = "Unanswered Mandatory Field";
    else if (e.min) message = "Minimum value not reached";
    else if (e.max) message = "Maximum value exceeded";
    else if (e.minlength) message = "Minimum length not reached";
    else if (e.maxlength) message = "Maximum length exceeded";
    return message;
  };

  taxReturn.categories.forEach((c: any) => {
    c.validationWarnings = [];
    c.subcategories.forEach(sc => {
      sc.validationWarnings = [];

      // have groups, only count if the group id is not 0
      sc.groups.filter(g => g.id !== 0 && !g.deleted).forEach(g => {
        const subGroupLookup = {};  // sgId: sg
        g.subgroups.forEach(sg => {
          subGroupLookup[sg.id] = sg;
        });

        const questionsForGroup = [];
        const questionsForGroupById = {};
        g.subgroups.forEach(sg => sg.questions.forEach(q => {
          const question = questionsById[q.id];
          const subgroupQuestion: any = {
            subGroupId: sg.id,
            ...question,
            answers: q.answers.map(a => answersById[a.id]),
          };
          questionsForGroup.push(subgroupQuestion);
          questionsForGroupById[subgroupQuestion.id] = subgroupQuestion;
        }));

        const validationWarnings = [];
        const vwLookup = {};

        questionsForGroup.forEach(q => {
          const parentId = q.dependantMandatoryParentId;
          const parentValue = q.dependantMandatoryParentValue;
          let hasDependantMandatoryError = false;

          if (!q.mandatory && parentId != null) {
            // dependent mandatory - specific value
            const parentQuestion = questionsForGroupById[parentId];
            if (parentQuestion == null) {
              // NOTE(mike): The subcategory page doesn't yet suppor dependant mandatory across subcategories so don't support it here (yet)
            } else if (parentQuestion.answers && parentQuestion.answers.length && parentQuestion.answers[0].answer === parentValue && typeof parentValue === 'string') {
                if (q.answers.length === 0 || !q.answers[0].answer) {
                  hasDependantMandatoryError = true;
                  validationWarnings.push({
                    ...q,
                    createdAtString: dateStr,
                    warningMessage: "Unanswered Mandatory Field"
                  });
                  vwLookup[q.id] = validationWarnings[validationWarnings.length - 1];
                }
            } else if(parentQuestion.answers && parentQuestion.answers.length && typeof parentValue !== 'string'){
              if (q.answers.length === 0 || !q.answers[0].answer) {
                hasDependantMandatoryError = true;
                validationWarnings.push({
                  ...q,
                  createdAtString: dateStr,
                  warningMessage: "Unanswered Mandatory Field"
                });
                vwLookup[q.id] = validationWarnings[validationWarnings.length - 1];
              }
            }
          }

          if (!hasDependantMandatoryError) {
            const subGroupId = q.subGroupId;
            const subGroup = subGroupLookup[subGroupId];
            const validator: ValidatorFn = Validators.compose(validatorsForQuestion(q));

            if (q && q.answers && q.answers.length) {
              // For rows of table Qs, we cache the answerIds actually present for faster lookup
              const sgRowAnswerLookup = {};
              if (subGroup.rows && subGroup.rows.length) {
                subGroup.rows.forEach((r, idx) => {
                  const cells = r.cells || {};
                  const ansId = cells[q.id].id;
                  if (ansId) {
                    sgRowAnswerLookup[ansId] = true;
                  }
                });
              }

              for (const answer of q.answers) {
                let answerValue = answer == null || answer.answer == null || answer.answer === "" ? null : answer.answer;
                // Do content validation
                if (q.dataType.toLowerCase() === 'float' || q.dataType.toLowerCase() === 'integer') {
                  answerValue = answerValue != null ? Number(answerValue) : answerValue;
                }

                const foundAnwserInRow = (subGroup.rows && subGroup.rows.length) ? !!sgRowAnswerLookup[answer.id] : false;

                if (answer.parentAnswerId === null || foundAnwserInRow) {
                  if (validator) {
                    const data: any = { value: answerValue };
                    const errors: ValidationErrors | null = validator(data);
                    if (errors) {
                      validationWarnings.push({
                        ...q,
                        createdAtString: dateStr,
                        warningMessage: getValidationMessage(errors),
                        answerId: answer?.id || null
                      });
                      vwLookup[q.id] = validationWarnings[validationWarnings.length - 1];
                    }
                  }
                }
              }
            } else {
              const answer = (q.answers && q.answers.length) ? q.answers[0] : null;
              let answerValue = answer == null || answer.answer == null || answer.answer === "" ? null : answer.answer;
              // Do content validation
              if (q.dataType.toLowerCase() === 'float' || q.dataType.toLowerCase() === 'integer') {
                answerValue = answerValue != null ? Number(answerValue) : answerValue;
              }
              if (validator) {
                const data: any = { value: answerValue };
                const errors: ValidationErrors | null = validator(data);
                if (errors) {
                  validationWarnings.push({
                    ...q,
                    createdAtString: dateStr,
                    warningMessage: getValidationMessage(errors),
                    answerId: answer?.id || null
                  });
                  vwLookup[q.id] = validationWarnings[validationWarnings.length - 1];
                }
              }
            }
          }
        });
        g.validationWarnings = validationWarnings;
        g.subgroups.forEach(sg => {
          const subgroupErrors = [];
          sg.questions.forEach(q => {
            const error = vwLookup[q.id];
            if (error) {
              subgroupErrors.push(error);
            }
          });
          sg.validationWarnings = subgroupErrors;
        });

        fastPushAll(sc.validationWarnings, g.validationWarnings);
      });

      fastPushAll(c.validationWarnings, sc.validationWarnings);
    });
  });
};

// Pushes all the items in array2 into array1 (i.e. array2.forEach(i => array1.push(i))
// This implementation is much better for large arrays
// Adapted from https://dev.to/uilicious/javascript-array-push-is-945x-faster-than-array-concat-1oki
const fastPushAll = (array1, array2) => {
  const array1Length = array1.length;
  array1.length = array1Length + array2.length;
  for (let i = 0; i < array2.length; i++) {
    array1[array1Length + i] = array2[i]
  }
}

export const setAssignedUser = (taxReturn: ITaxReturn) => {
  let assignedUser = null;

  const status = TaxReturnStatus.byId(taxReturn.status, GlobalConfig.tenant);
  const preparers = taxReturn.preparer || [];
  const reviewers = taxReturn.reviewer || [];

  if (status.inPrep) {
    const allPreparers = preparers.map(p => ({
      id: p.id,
      name: makeName(p),
      firstName: p.firstName,
      lastName: p.lastName,
      isPreparer: true,
      isReviewer: false,
      status: TaxReturnStatus.byId(p.preparers.status, GlobalConfig.tenant),
      updatedAt: new Date(p.preparers.updatedAt),
    }));
    const preparer = maxBy(allPreparers, p => p.updatedAt);
    assignedUser = preparer;
  } else if (status.inReview) {
    const allReviewers = reviewers.map(r => ({
      id: r.id,
      name: makeName(r),
      firstName: r.firstName,
      lastName: r.lastName,
      isPreparer: false,
      isReviewer: true,
      status: TaxReturnStatus.byId(r.reviewers.status, GlobalConfig.tenant),
      updatedAt: new Date(r.reviewers.updatedAt),
    }));
    const reviewer = maxBy(allReviewers, r => r.updatedAt);
    assignedUser = reviewer;
  } else {
    assignedUser = null;
  }
  taxReturn.assignedUser = assignedUser;
};

export const setStatus = (taxReturn: ITaxReturn) => {
  taxReturn.fullStatus = TaxReturnStatus.byId(taxReturn.status, GlobalConfig.tenant);
  const { complexityField } = taxReturn.fullStatus.step;
  if (complexityField != null) {
    taxReturn.statusComplexity = taxReturn[complexityField];
  } else {
    taxReturn.statusComplexity = null;
  }
};

export const handleEmptyOrDeletedGroups = (taxReturn: ITaxReturn, includeDeleted: boolean) => {
  (taxReturn.categories || []).forEach(c => c.subcategories.forEach(sc => {
    const tabQuestion = sc.questions.find(q => taxReturn.questionsById[q.id].isTab);
    if (tabQuestion) {
      sc.groups.forEach(g => {
        if (isGroupDeleted(taxReturn, g)) {
          g.deleted = true;
          if (isGhost(taxReturn, g)) {
            g.isGhost = true;
          }
        }
      });
      // filter out ghost tabs, i.e. empty answers and the tab is not an add-override-tab
      const filteredGroups = includeDeleted ? sc.groups : sc.groups.filter(g => !g.isGhost);
      if (filteredGroups.length === 0) {
        // put default tab back
        const g = sc.groups[0];
        g.subgroups.forEach(sg => {
          sg.commentCount = 0;
          sg.flagCount = 0;
          sg.warningCount = 0;
          sg.questions.forEach(q => {
            q.answers = [];
          });
        });
        g.id = 0;
        g.name = null;
        g.commentCount = 0;
        g.flagCount = 0;
        g.warningCount = 0;
        delete g.deleted;
        delete g.isGhost;
        filteredGroups.push(g);
      }
      sc.groups = filteredGroups;
    }
  }));
};

const applySnapshotId = (taxReturn: ITaxReturn, snapshotId: number | null) => {
  const snapshot: any = snapshotId != null ? taxReturn.taxReturnSnapshots.find(ss => ss.id === snapshotId) : null;
  if (snapshot) {
    taxReturn.status = snapshot.oldStatus;
    taxReturn.complexityPreparer = snapshot.complexityPreparer;
    taxReturn.complexityReviewer1 = snapshot.complexityReviewer1;
    taxReturn.complexityReviewer2 = snapshot.complexityReviewer2;

    const snapshotDate = moment(snapshot.createdAt);
    snapshot.createdAtStr = snapshotDate.format(dateFormat);

    const qsById = taxReturn.questionsById;
    const asById = taxReturn.answersById;

    const answerIdsToDelete = {};
    Object.values(asById).forEach((answer: any) => {
      answer.answerAudits.sort((a, b) => a.id - b.id);
      let latestAnswerAudit = null;
      for (const aa of answer.answerAudits) {
        if (moment(aa.createdAt).isBefore(snapshotDate)) {
          latestAnswerAudit = aa;
        } else {
          break;
        }
      }
      if (latestAnswerAudit == null) {
        answerIdsToDelete[answer.id] = true;
      } else {
        const q = qsById[answer.questionId];
        answer.answer = latestAnswerAudit.newAnswer || "";

        // determine override status, take it from the answerAudit if possible, for older data just try to infer it
        if (latestAnswerAudit.newOverrideStatus != null) {
          answer.overrideStatus = latestAnswerAudit.newOverrideStatus;
        } else if (q.APSCalcField) {
          answer.overrideStatus = 3;
        } else if (latestAnswerAudit.userId == null) {
          answer.overrideStatus = 0;
        } else {
          // We can derive that the tab was manually added if the first answer audit was by a person
          const isTab = q.isTab;
          const firstAnswerAuditUserId = answer.answerAudits[0].userId;
          answer.overrideStatus = isTab && firstAnswerAuditUserId ? 2 : 1;
        }

        const comments = [];
        for (const c of answer.comments) {
          if (moment(c.createdAt).isBefore(snapshotDate)) {
            comments.push(c);
          }
        }
        answer.comments = comments;

        const answerWarnings = [];
        for (const aw of answer.answerWarnings) {
          if (moment(aw.createdAt).isBefore(snapshotDate)) {
            answerWarnings.push(aw);
            if (aw.status !== 0) {
              if (aw.closedAt && moment(aw.closedAt).isSameOrAfter(snapshotDate)) {
                // was closed after
                aw.status = 0;
              }
            }
          }
        }
        answer.answerWarnings = answerWarnings;

        const answerFlags = [];
        for (const af of answer.answerFlags) {
          if (moment(af.createdAt).isBefore(snapshotDate)) {
            answerFlags.push(af);
            if (af.status !== 0) {
              if (af.closedAt && moment(af.closedAt).isSameOrAfter(snapshotDate)) {
                // was closed after
                af.status = 0;
              }
            }
          }
        }
        answer.answerFlags = answerFlags;
      }
    });
    Object.values(qsById).forEach((q: any) => {
      q.answers = q.answers.filter(a => !answerIdsToDelete[a.id]);
    });
  }
  return snapshot;
};

export const resolveDocuments = (taxReturnService: TaxReturnService, item: ITaxReturn | Parts): Observable<void> => {
  const unresolvedDocIds = item.documents.map(doc => doc.id).filter(docId => !inDocumentCache(docId));
  if (unresolvedDocIds.length) {
    return Observable.create((observer) => {
      from(unresolvedDocIds.map(encodeURIComponent)).pipe(
        bufferCount(5),
        mergeMap((chunkDocIds: string[]) => taxReturnService.getDocumentMetadata(chunkDocIds)),
      ).subscribe(resp => {
        updateDocumentCache(resp.results);
        // update documents
        item.documents = item.documents.map(getResolvedDocument);
        observer.next(resp);
        observer.complete();
      });
    })
  } else {
    // update documents
    item.documents = item.documents.map(getResolvedDocument);
    return of();
  }
};

export const handleUrlsForNewComment = (taxReturnService: TaxReturnService, taxReturn: ITaxReturn, newComment: IComment) => {
  const data = parseMessageForDocumentsAndUrls(newComment.message, newComment.attachments);
  newComment.parts = data.parts;
  newComment.documents = data.documents;
  newComment.attachments = data.attachments;

  if (newComment.documents.length) {
    const docsById = {};
    taxReturn.documents.forEach(d => {
      docsById[d.id] = d;
    });

    const newDocIds = [];
    data.documents.forEach(doc => {
      if (!docsById[doc.id]) {
        newDocIds.push(doc.id);
        docsById[doc.id] = doc;
      }
    });

    taxReturn.documents = Object.values(docsById);
    return resolveDocuments(taxReturnService, taxReturn).pipe(map(() => true));
  } else {
    return of(false);
  }
};

export const getClientRulesWarningMsg = (mandatoryIncompleteCount: number) => {
  if (mandatoryIncompleteCount > 1) {
    return `There are ${mandatoryIncompleteCount} unanswered mandatory client rule questions`;
  }
  if (mandatoryIncompleteCount === 1) {
    return `There is ${mandatoryIncompleteCount} unanswered mandatory client rule question`;
  }
}

export const initializeTaxReturn = (taxReturn: ITaxReturn, snapshotId: number | null = null, requestFullData: boolean = false) => {
  setQuestionAndAnswerLookups(taxReturn);
  const snapshot = applySnapshotId(taxReturn, snapshotId);
  initCommentUrlsAndDocuments(taxReturn);
  if (taxReturn.categories) {
    initQuestions(taxReturn);
    handleEmptyOrDeletedGroups(taxReturn, requestFullData);
    initTables(taxReturn);
    initQuestionsAndCounts(taxReturn);
    getValidationWarnings(taxReturn);
    if(!taxReturn.categoryCounts){
      getValidationWarnings(taxReturn);
    }
  }
  setAssignedUser(taxReturn);
  setStatus(taxReturn);
  taxReturn.fullData = requestFullData;
  taxReturn.snapshotId = snapshotId;
  taxReturn.snapshot = snapshot;
};
